
import { AIContentConfig, TopicStructure, RecipeData } from "../types";
import { SUPREME_EDITORIAL_BRAIN, TITLE_MACHINE_PROMPT, RECIPE_BRAIN, BIBLE_STUDY_BRAIN } from "./prompts";
import { API_URL } from "../constants";

const getOpenAiUrl = (provider: string = 'openai', endpoint: string = 'chat/completions') => {
    return `${API_URL}?action=ai_bridge&provider=${provider}&endpoint=${endpoint}`;
};

const stripTags = (text: string): string => {
    return text
        .replace(/<[^>]*>?/gm, '')
        .replace(/^[0-9.\-\*\s•]+/, '') // Limpeza de marcadores
        .replace(/^"|"$/g, '')
        .replace(/&nbsp;/g, ' ')
        .trim();
};

const cleanAiHtml = (raw: string): string => {
    let clean = raw
        .replace(/```html|```/gi, "") 
        .replace(/<!DOCTYPE html>/gi, "")
        .replace(/<html[^>]*>/gi, "")
        .replace(/<\/html>/gi, "")
        .replace(/<head[^>]*>[\s\S]*?<\/head>/gi, "")
        .replace(/<body[^>]*>/gi, "")
        .replace(/<\/body>/gi, "")
        .replace(/<h1[^>]*>[\s\S]*?<\/h1>/gi, "");

    // Strip SEO blocks that AI sometimes outputs at the end
    const seoPatterns = [
        /Meta Title:[\s\S]*?$/i,
        /Meta Description:[\s\S]*?$/i,
        /Slug:[\s\S]*?$/i,
        /Keywords:[\s\S]*?$/i,
        /Palavras-chave:[\s\S]*?$/i,
        /Intenção de busca:[\s\S]*?$/i,
        /Clusters internos:[\s\S]*?$/i,
        /Sugestão de clusters:[\s\S]*?$/i
    ];

    seoPatterns.forEach(pattern => {
        clean = clean.replace(pattern, '');
    });

    return clean.trim();
};

const buildSystemInstruction = (config?: AIContentConfig, siteContext?: string) => {
    let prompt = SUPREME_EDITORIAL_BRAIN;
    
    // Extrair título e descrição do contexto se possível
    const siteTitle = siteContext?.match(/Site: (.*?) -/)?.[1] || 'este site';
    const siteDescription = siteContext?.match(/- (.*)$/)?.[1] || 'um portal de conteúdo especializado';

    prompt = prompt
        .replace(/{{siteTitle}}/g, siteTitle)
        .replace(/{{siteDescription}}/g, siteDescription);

    return `${prompt}
    CONTEXTO DO SITE:
    ${siteContext || 'Não fornecido'}

    CONFIGURAÇÕES ADICIONAIS:
    - Tom: ${config?.tone || 'Profissional'}
    - Público: ${config?.audience || 'Geral'}
    - Personalização: ${config?.customPrompt || 'Nenhuma'}
    `;
};

// Função utilitária para retry com backoff exponencial
const withRetry = async <T>(fn: () => Promise<T>, maxRetries = 3): Promise<T> => {
    let lastError: any;
    for (let i = 0; i < maxRetries; i++) {
        try {
            return await fn();
        } catch (error: any) {
            lastError = error;
            // Se for erro 429 (Rate Limit) ou 5xx, tenta de novo
            const isRetryable = error.message?.includes('429') || 
                               error.message?.includes('500') || 
                               error.message?.includes('502') || 
                               error.message?.includes('503') || 
                               error.message?.includes('504');
            
            if (isRetryable && i < maxRetries - 1) {
                const delay = Math.pow(2, i) * 1000 + Math.random() * 1000;
                console.warn(`OpenAI ocupado (${error.message}). Tentativa ${i + 1} de ${maxRetries}. Aguardando ${Math.round(delay)}ms...`);
                await new Promise(resolve => setTimeout(resolve, delay));
                continue;
            }
            throw error;
        }
    }
    throw lastError;
};

const callOpenAI = async (apiKey: string, system: string, user: string, jsonMode: boolean = false, provider: string = 'openai'): Promise<string> => {
    return withRetry(async () => {
        let model = "gpt-4o";
        if (provider === 'deepseek') model = "deepseek-chat";
        if (provider === 'groq') model = "llama3-70b-8192";

        const body: any = {
            model: model,
            messages: [{ role: "system", content: system }, { role: "user", content: user }],
            temperature: 0.7
        };
        if (jsonMode) body.response_format = { type: "json_object" };

        const res = await fetch(getOpenAiUrl(provider), {
            method: 'POST',
            headers: { 
                'Content-Type': 'application/json',
                ...(apiKey ? { 'Authorization': `Bearer ${apiKey}` } : {})
            },
            body: JSON.stringify(body)
        });
        const data = await res.json();
        if (data.error) throw new Error(data.error.message);
        return data.choices[0]?.message?.content || "";
    });
};

export const generateOpenAITopicsStructure = async (theme: string, topicsCount: number, subtopicsCount: number, apiKey: string, existingTitles: string[] = [], siteContext?: string, provider: string = 'openai'): Promise<TopicStructure[]> => {
    const avoidList = existingTitles.length > 0 ? `\n\nEVITE ESTES TÍTULOS (JÁ EXISTEM):\n${existingTitles.join('\n')}` : '';
    const system = `${buildSystemInstruction(undefined, siteContext)} Você é um Arquiteto de Conteúdo SEO. Retorne estritamente um JSON com a chave 'topics'.`;
    const user = `Crie a estrutura completa de pauta para o tema: "${theme}". 
    Gere exatamente ${topicsCount} tópicos principais (H2).
    Para CADA tópico principal, gere exatamente ${subtopicsCount} sub-tópicos (H3) detalhados e relevantes.
    Formato esperado: {"topics": [{"id": "1", "title": "Título do H2", "subtopics": [{"id": "1-1", "text": "Título do H3"}]}]}${avoidList}`;
    const res = await callOpenAI(apiKey, system, user, true, provider);
    try {
        const parsed = JSON.parse(res);
        return parsed.topics || parsed;
    } catch { return []; }
};

export const generateOpenAIArticleFromTopics = async (title: string, category: string, topics: TopicStructure[], apiKey: string, config?: AIContentConfig, siteContext?: string, provider: string = 'openai'): Promise<string> => {
    const system = buildSystemInstruction(config, siteContext);
    const structure = topics.map(t => `H2: ${t.title}\n${t.subtopics.map(s => `- H3: ${s.text}`).join('\n')}`).join('\n\n');
    const res = await callOpenAI(apiKey, system, `Escreva o ARTIGO PILAR MUITO COMPLETO E DETALHADO sobre "${title}". Siga rigorosamente a estrutura abaixo e TODAS as instruções do System Prompt de DIAGRAMAÇÃO EXTREMA, usando <p> para separar parágrafos e OBRIGATORIAMENTE injetando as marcações <div class="ai-summary-box">, <div class="box-destaque"> e <div class="cta-box"> ao longo do HTML.\nESTRUTURA:\n${structure}`, false, provider);
    return cleanAiHtml(res);
};

export const generateOpenAITitlesList = async (topic: string, count: number, apiKey: string, existingTitles: string[] = [], provider: string = 'openai'): Promise<string[]> => {
    const system = TITLE_MACHINE_PROMPT;
    const avoidList = existingTitles.length > 0 ? `\n\n⚠️ PROIBIDO USAR ESTES TÍTULOS (JÁ EXISTEM):\n${existingTitles.join('\n')}` : '';
    const res = await callOpenAI(apiKey, system, `Lista estratégica de ${count} títulos magnéticos e autoritativos sobre "${topic}". Siga rigorosamente as regras do System Prompt.${avoidList}`, false, provider);
    return res.split('\n').map(t => stripTags(t)).filter(t => t.length > 5);
};

export const generateOpenAINewsExcerpt = async (title: string, apiKey: string, config?: AIContentConfig, provider: string = 'openai'): Promise<string> => {
    const system = "Você é um Especialista em SEO e Copywriting. Crie resumos curtos, magnéticos e diretos ao ponto.";
    const res = await callOpenAI(apiKey, system, `Resumo SEO Master (30 palavras) para: "${title}". Texto puro.`, false, provider);
    return stripTags(res);
};

export const generateOpenAIFullArticle = async (title: string, category: string, apiKey: string, config?: AIContentConfig, siteContext?: string, provider: string = 'openai'): Promise<string> => {
    const system = buildSystemInstruction(config, siteContext);
    const res = await callOpenAI(apiKey, system, `Escreva o ARTIGO PILAR MUITO COMPLETO E DETALHADO sobre "${title}". Siga rigorosamente TODAS as instruções do System Prompt de DIAGRAMAÇÃO EXTREMA, usando <p> para separar parágrafos e OBRIGATORIAMENTE injetando as marcações <div class="ai-summary-box">, <div class="box-destaque"> e <div class="cta-box"> ao longo do HTML.`, false, provider);
    return cleanAiHtml(res);
};

export const generateOpenAIInternalLinks = async (contentHTML: string, availableTargets: { title: string; url: string; type: 'post' | 'product'; id: string }[], apiKey: string, provider: string = 'openai'): Promise<any[]> => {
    const targets = availableTargets.map(t => `- ID: ${t.id}, Título: "${t.title}" (URL: ${t.url})`).join("\n");
    const system = "Você é um Especialista em SEO Técnico e Arquitetura de Informação. Retorne estritamente um JSON com a chave 'suggestions' contendo uma lista de objetos.";
    const user = `Analise o texto e sugira links internos JSON. Identifique frases ou palavras-chave no HTML que podem ser usadas como âncora para um link para um dos artigos da LISTA DE ARTIGOS.
    
    REGRAS ESTRITAS:
    1. Retorne APENAS um JSON válido.
    2. O JSON deve ser uma lista de objetos na chave 'suggestions'.
    3. Cada objeto deve ter 'frase' (a frase exata encontrada no texto principal), 'artigo_sugerido_id' (o ID do artigo correspondente), 'targetUrl' (a URL do artigo correspondente) e 'targetTitle' (o título do artigo correspondente).
    
    LISTA DE ARTIGOS (para linkar):
    ${targets}
    
    HTML:
    ${contentHTML.substring(0, 4000)}`;
    const res = await callOpenAI(apiKey, system, user, true, provider);
    try { 
        const parsed = JSON.parse(res); 
        return parsed.suggestions || parsed.links || parsed;
    } catch { return []; }
};

export const generateOpenAIProductDescription = async (productName: string, apiKey: string, provider: string = 'openai'): Promise<string> => {
    const system = "Você é um Copywriter Especialista em Vendas. Seja persuasivo e conciso.";
    const res = await callOpenAI(apiKey, system, `Copy persuasiva de 20 palavras para: ${productName}. Texto puro.`, false, provider);
    return stripTags(res);
};

export const generateOpenAIMetaDescription = async (title: string, apiKey: string, provider: string = 'openai'): Promise<string> => {
    const system = "Você é um Especialista em SEO e Copywriting.";
    const res = await callOpenAI(apiKey, system, `Crie uma meta descrição SEO perfeita, persuasiva e otimizada para o Google (máximo 160 caracteres) para um site com o título: "${title}". A descrição deve ser atraente e incentivar o clique. Retorne apenas o texto da descrição, sem aspas ou explicações.`, false, provider);
    return stripTags(res);
};

export const generateOpenAILocalSeoArticle = async (service: string, city: string, apiKey: string, whatsapp?: string, siteContext?: string, provider: string = 'openai'): Promise<string> => {
    const system = buildSystemInstruction(undefined, siteContext);
    const res = await callOpenAI(apiKey, system, `Escreva o ARTIGO PILAR MUITO COMPLETO E DETALHADO (Landing Page de Autoridade) sobre "${service}" em "${city}". Siga rigorosamente as instruções do System Prompt.`, false, provider);
    return cleanAiHtml(res);
};

export const generateOpenAILocalAuthorityArticles = async (service: string, city: string, pillarUrl: string, apiKey: string, siteContext?: string, provider: string = 'openai'): Promise<{ title: string; content: string }[]> => {
    const system = `${buildSystemInstruction(undefined, siteContext)} Você é um Especialista em SEO de Autoridade Regional. Retorne estritamente um JSON com a chave 'articles'.`;
    const user = `Gere 2 artigos de apoio (Cluster de Autoridade) para o serviço "${service}" na cidade de "${city}".
    
    IMPORTANTE: Você DEVE incluir pelo menos um link interno para a página principal (Pillar) usando a URL: ${pillarUrl}.
    
    Artigo 1: Panorama Digital/Econômico da cidade.
    Artigo 2: Dores/Dicas para empresas locais crescerem com ${service}.
    Formato: {"articles": [{"title": "Título", "content": "HTML"}]}`;
    const res = await callOpenAI(apiKey, system, user, true, provider);
    try {
        const parsed = JSON.parse(res);
        return (parsed.articles || []).map((a: any) => ({
            title: a.title,
            content: cleanAiHtml(a.content)
        }));
    } catch { return []; }
};

export const generateOpenAIViralTitlesByCategory = async (
    category: string, 
    count: number = 10, 
    apiKey: string, 
    existingTitles: string[] = [], 
    provider: string = 'openai',
    brain?: string,
    customTopic?: string
): Promise<string[]> => {
    const avoidList = existingTitles.length > 0 ? `\n\n⚠️ PROIBIDO USAR ESTES TÍTULOS (JÁ EXISTEM NO BANCO):\n${existingTitles.join('\n')}` : '';
    
    let brainInstruction = '';
    if (brain) {
        switch (brain) {
            case 'educativo':
                brainInstruction = `\n🎯 ESTILO DO CÉREBRO: EDUCATIVO / AUTORIDADE\nFoco em ensinar algo novo, decodificar conceitos complexos, trazer fatos de autoridade e educar o público. Faça com que o leitor sinta que vai aprender algo valioso imediatamente. Use ganchos como "Como funciona...", "Guia Completo para...", "O que ninguém te conta sobre...", "A Ciência por trás de...".`;
                break;
            case 'comparativo':
                brainInstruction = `\n🎯 ESTILO DO CÉREBRO: COMPARATIVO (ANTES VS DEPOIS / MITO VS VERDADE)\nFoco em contrastar dois cenários polares, mitos vs verdades, ou comparar Produto A vs Produto B. Use ganchos como "[Assunto] vs [Alternativa]: Qual realmente vale a pena?", "Mito ou Verdade sobre...", "O Antes e Depois de...", "Por que o método X supera o Y".`;
                break;
            case 'tendencias':
                brainInstruction = `\n🎯 ESTILO DO CÉREBRO: ANÁLISE DE TENDÊNCIAS / ALERTA\nFoco nas novidades do mercado para 2026, atualizações quentes, notícias urgentes e previsões revolucionárias. Toque na curiosidade e no sentimento de novidade. Use ganchos como "A nova tendência de... para 2026", "Alerta: O que muda com...", "Não ignore isso: O futuro do...".`;
                break;
            case 'cases':
                brainInstruction = `\n🎯 ESTILO DO CÉREBRO: CASES DE SUCESSO / PROVA SOCIAL\nFoco em histórias da vida real, estudos de caso inspiradores, faturamento, conquistas mensuráveis e prova social incontestável. Use ganchos como "Como conseguiu [Resultado] em poucos dias", "Estudo de caso de sucesso em...", "O que aprendemos com a história real de...".`;
                break;
            case 'listas':
                brainInstruction = `\n🎯 ESTILO DO CÉREBRO: LISTAS / GANCHOS MAGNÉTICOS\nFoco em formatos escaneáveis de listas, números ímpares, ferramentas úteis, erros a evitar e segredos revelados. Use ganchos como "7 Erros graves ao...", "11 Ferramentas para...", "5 Dicas práticas de...", "3 Segredos de...".`;
                break;
            case 'copy':
                brainInstruction = `\n🎯 ESTILO DO CÉREBRO: COPYWRITING PERSUASIVO & CURIOSIDADE EXTRAORDINÁRIA\nFoco absoluto em micro-narrativas de suspense, curiosidade insaciável, psicologia reversa e o desejo instintivo de abrir loops de mistério. Use ganchos como "O segredo oculto por trás de...", "O que acontece quando você...", "A verdade chocante que nunca te contaram sobre...".`;
                break;
            case 'vendas':
                brainInstruction = `\n🎯 ESTILO DO CÉREBRO: ESTRATÉGIA DE VENDA / CONVERSÃO\nFoco comercial direto ou indireto, desejo de compra, bônus fantásticos, quebra de objeções clássicas de vendas, análise de se vale a pena comprar e ofertas vantajosas. Use ganchos como "Vale a pena comprar o...?", "Como economizar com...", "O Guia de Compra Definitivo de...".`;
                break;
            case 'storytelling':
                brainInstruction = `\n🎯 ESTILO DO CÉREBRO: HISTÓRIAS / STORYTELLING ENVOLVENTE\nFoco em narrativa rica em emoção humana, vulnerabilidade, jornada heróica, erros, superações pessoais e empatia profunda. Use ganchos como "A história não contada de...", "Como eu quase perdi tudo antes de...", "O dia em que descobri que...".`;
                break;
            case 'checklists':
                brainInstruction = `\n🎯 ESTILO DO CÉREBRO: CHECKLISTS / PASSO A PASSO PRÁTICO\nFoco em guias super acionáveis, checklists objetivos, receitas de sucesso diretas e planos simples no estilo passo-a-passo sem rodeios. Use ganchos como "Passo a Passo: Como fazer...", "O checklist definitivo para...", "Cronograma prático para...".`;
                break;
        }
    }

    const customTopicInstruction = customTopic ? `\n💡 ASSUNTO PARCEIRO / INSTRUÇÕES ADICIONAIS DO USUÁRIO:\nFoque exatamente neste assunto, dor ou instruções que o usuário digitou: "${customTopic}". Mescle este tópico com a categoria de forma extremamente orgânica.` : '';

    const prompt = `
Você é um especialista global em SEO, Copywriting Persuasivo e Otimização de CTR para Google.

Seu papel é gerar títulos estratégicos altamente clicáveis, baseados na categoria e palavra-chave fornecidas pelo usuário.

Você deve se adaptar automaticamente a qualquer nicho, mercado ou segmento.
${avoidList}
${brainInstruction}
${customTopicInstruction}

🔎 ETAPA 1 – ANÁLISE INTELIGENTE (OBRIGATÓRIA)

Antes de gerar os títulos:

Analise a categoria informada: "${category}"
${customTopic ? `Analise também o assunto do usuário: "${customTopic}"` : ''}

Identifique automaticamente:
- Dores do público
- Desejos do público
- Nível de consciência (iniciante, intermediário, avançado)
- Tipo de linguagem ideal (técnica, comercial, emocional, educativa)

Considere que o conteúdo precisa competir com a primeira página do Google e redes sociais.
Não exiba essa análise. Apenas use internamente.

🎯 REGRAS OBRIGATÓRIAS PARA OS TÍTULOS

Palavra-chave ou assunto principal deve aparecer de forma visível e atrativa nos títulos.
Evitar títulos genéricos e sem graça. Retorne ideias magnéticas.
Evitar clickbait enganoso que não entrega o valor prometido.
Usar números específicos quando fizer sentido.
Focar sempre em benefício claro e imediato para o leitor.
Criar curiosidade legítima.
Linguagem natural, humana (estilo UGC, recomendação pessoal relevante).
Máximo de 65 caracteres quando possível.
Variar estruturas estratégicas e não repetir padrões.

📅 REGRA DE DATA: PROIBIDO usar "2024". Use "2026" ou termos como "Atualizado", "Hoje", "Agora".

Gere exatamente ${count} sugestões de títulos incríveis de acordo com as especificidades do CÉREBRO e do ASSUNTO fornecido acima (caso tenham sido definidos).

REGRAS DE SAÍDA:
- Retorne APENAS a lista de títulos, um por linha.
- NÃO use numeração (1., 2., etc) ou marcadores (-).
- NÃO inclua categorias ou cabeçalhos na saída.
- NÃO use aspas.
`;
    const res = await callOpenAI(apiKey, "Você é uma Máquina de Títulos Virais especializada no cérebro selecionado. Retorne apenas texto puro, um título por linha.", prompt, false, provider);
    return res.split('\n').map(l => stripTags(l)).filter(l => l.length > 5);
};

export const generateOpenAIViralTitles = async (currentTitle: string, apiKey: string, existingTitles: string[] = [], provider: string = 'openai'): Promise<string[]> => {
    const avoidList = existingTitles.length > 0 ? `\n\n⚠️ PROIBIDO USAR ESTES TÍTULOS:\n${existingTitles.join('\n')}` : '';
    const res = await callOpenAI(apiKey, TITLE_MACHINE_PROMPT, `5 títulos magnéticos para: "${currentTitle}". Texto puro. Sem traços no início.${avoidList}`, false, provider);
    return res.split('\n').map(l => stripTags(l)).filter(l => l.length > 5);
};

export const generateOpenAIVisualPromptSlogan = async (title: string, context: string, apiKey: string, provider: string = 'openai'): Promise<string> => {
    const res = await callOpenAI(apiKey, "Expert Visual Prompt Engineer.", `Create a detailed, high-impact visual prompt in English for a COVER IMAGE of an article titled: "${title}". The prompt MUST describe a LITERAL, REALISTIC scene of everyday life (e.g. real people working, office environments, normal human interactions). STRICTLY FORBIDDEN: Do NOT use symbolic metaphors, abstract concepts, glowing lights, holograms, or surreal elements. It must look true-to-life and grounded in reality. ABSOLUTELY NO TEXT, NO LETTERS, NO WORDS, NO LOGOS in the image. Describe colors, natural lighting, and photographic composition. Additional context: ${context}. Return ONLY the prompt in English, no quotes or explanations. Suffix the prompt with: ", no text, no watermark, photorealistic"`, false, provider);
    return stripTags(res);
};

export const generateOpenAIImage = async (prompt: string, apiKey: string, aspectRatio?: string, provider: string = 'openai'): Promise<string> => {
    return await withRetry(async () => {
        let size = "1024x1024";
        if (aspectRatio === "9:16") {
            size = "1024x1792";
        } else if (aspectRatio === "16:9") {
            size = "1792x1024";
        }

        try {
            const res = await fetch(getOpenAiUrl(provider, 'images/generations'), {
                method: 'POST',
                headers: { 
                    'Content-Type': 'application/json',
                    ...(apiKey ? { 'Authorization': `Bearer ${apiKey}` } : {})
                },
                body: JSON.stringify({ model: "dall-e-3", prompt: `High-end magazine photo: ${prompt}`, n: 1, size: size, response_format: "b64_json" })
            });
            const data = await res.json();
            if (data.error) {
                throw new Error(data.error.message || JSON.stringify(data.error));
            }
            if (data.data && data.data[0]) {
                if (data.data[0].b64_json) {
                    return `data:image/png;base64,${data.data[0].b64_json}`;
                } else if (data.data[0].url) {
                    return data.data[0].url;
                }
            }
            throw new Error("No image data found in first attempt");
        } catch (err: any) {
            const errorMsg = err.message || '';
            console.warn("[AI Factory] OpenAI Image generation failed with response_format, retrying without it:", errorMsg);
            
            // Tenta novamente sem o parâmetro 'response_format'
            const res = await fetch(getOpenAiUrl(provider, 'images/generations'), {
                method: 'POST',
                headers: { 
                    'Content-Type': 'application/json',
                    ...(apiKey ? { 'Authorization': `Bearer ${apiKey}` } : {})
                },
                body: JSON.stringify({ model: "dall-e-3", prompt: `High-end magazine photo: ${prompt}`, n: 1, size: size })
            });
            const data = await res.json();
            if (data.error) throw new Error(data.error.message || JSON.stringify(data.error));
            
            if (data.data && data.data[0]) {
                if (data.data[0].url) {
                    return data.data[0].url;
                } else if (data.data[0].b64_json) {
                    return `data:image/png;base64,${data.data[0].b64_json}`;
                }
            }
            throw new Error("No image data found in OpenAI fallback request: " + JSON.stringify(data));
        }
    });
};

export const rewriteContent = async (rawText: string, apiKey: string, config?: AIContentConfig, siteContext?: string, provider: string = 'openai'): Promise<{ title: string; content: string; excerpt: string }> => {
    // 1. Generate Better Title
    const titleSystem = TITLE_MACHINE_PROMPT;
    const titleUser = `Analise o texto abaixo e crie UM ÚNICO Título Viral e Magnético (SEO Otimizado) para ele seguindo as regras do System Prompt. Retorne APENAS o texto do título.\n\nTEXTO BASE:\n${rawText.substring(0, 3000)}`;
    const titleRes = await callOpenAI(apiKey, titleSystem, titleUser, false, provider);
    // Pega apenas a primeira linha caso a IA ignore e mande várias
    const newTitle = stripTags((titleRes || "Sem Título").split('\n')[0]);

    // 2. Generate Content (HTML) - 10k words style
    const system = buildSystemInstruction(config, siteContext);
    const contentPrompt = `Atue como um Editor Chefe Sênior. IMPORTANTE: Conclua e finalize a formatação corretamente. Não corte o texto no meio e feche todas as tags HTML. Reescreva o seguinte texto transformando-o em um ARTIGO PILAR MUITO COMPLETO E DETALHADO. Use o texto base apenas como inspiração e fonte de dados, mas expanda e enriqueça (limite máximo de 2500 palavras para evitar corte na resposta e garanta fechamento das tags) com seu conhecimento, seguindo a estrutura e qualidade do System Prompt.\n\nTEXTO BASE:\n"${rawText.substring(0, 5000)}"`;
    
    const contentRes = await callOpenAI(apiKey, system, contentPrompt, false, provider);
    const generatedHtml = cleanAiHtml(contentRes);

    // 3. Generate Excerpt
    const excerpt = await generateOpenAINewsExcerpt(newTitle, apiKey, config, provider);

    return {
        title: newTitle,
        content: generatedHtml,
        excerpt: excerpt
    };
};

export const generateOpenAIBusinessData = async (prompt: string, apiKey: string, provider: string = 'openai'): Promise<any> => {
    const system = "Você é um Especialista em Inteligência de Negócios. Sua tarefa é encontrar e extrair informações extremamente detalhadas sobre empresas. Retorne estritamente um JSON válido.";
    const res = await callOpenAI(apiKey, system, prompt, true, provider);
    try {
        return JSON.parse(res);
    } catch (e) {
        console.error("Erro ao parsear resposta de negócio da OpenAI:", e);
        return {};
    }
};

export const generateOpenAIReview = async (systemPrompt: string, userPrompt: string, apiKey: string, provider: string = 'openai'): Promise<string> => {
    return await callOpenAI(apiKey, systemPrompt, userPrompt, false, provider);
};

export const generateOpenAIGeneric = async (prompt: string, apiKey: string, provider: string = 'openai'): Promise<string> => {
    const res = await callOpenAI(apiKey, "Você é um assistente de IA útil e preciso.", prompt, false, provider);
    return stripTags(res);
};

export const generateOpenAIChatResponse = async (history: any[], message: string, context: string, apiKey: string, provider: string = 'openai', siteTitle: string = 'este site'): Promise<string> => {
    const systemPrompt = `
    VOCÊ É O MENTOR DIGITAL E CONSULTOR MASTER DE ${siteTitle.toUpperCase()}.
    
    IDENTIDADE E ATITUDE:
    - Seja extremamente cordial, prestativo e educado. Use termos como "Com certeza", "É um prazer ajudar" e "Excelente pergunta".
    - Sua missão é guiar o usuário pelas soluções de ${siteTitle}.
    
    O SEGREDO DO SITE:
    - Se perguntarem "como fazer um site assim" ou "como isso foi feito", responda com entusiasmo: 
      "O segredo deste ecossistema é a Automação Inteligente desenvolvida para ${siteTitle}. Ele une o poder da IA com arquiteturas de alta performance para criar ferramentas que vendem sozinhas. Você pode ter um igual acessando nossa vitrine de [Produtos](/?pagina=produtos) ou solicitando um projeto personalizado em [Contato](/?pagina=contato)."
    
    DIRETRIZES DE RESPOSTA:
    - Use os links fornecidos no contexto abaixo para recomendar produtos ou artigos relevantes. 
    - Responda apenas o que for relacionado ao nicho de ${siteTitle}, tecnologia, SEO e automação.
    - NUNCA revele segredos técnicos de implementação privada ou dados sensíveis de outros usuários.
    - Se não souber algo, direcione amigavelmente para o WhatsApp de suporte oficial.
    
    CONTEXTO DO SITE ATUAL:
    ${context}
    `;

    try {
        // Converte o histórico para o formato da OpenAI
        const messages = [
            { role: "system", content: systemPrompt },
            ...history.map(h => ({
                role: h.role === 'model' ? 'assistant' : 'user',
                content: h.parts[0].text
            })),
            { role: "user", content: message }
        ];

        let model = "gpt-4o";
        if (provider === 'deepseek') model = "deepseek-chat";
        if (provider === 'groq') model = "llama3-70b-8192";

        const res = await fetch(getOpenAiUrl(provider), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                model: model,
                messages: messages,
                temperature: 0.7
            })
        });
        const data = await res.json();
        if (data.error) throw new Error(data.error.message);
        return data.choices[0]?.message?.content || "";
    } catch (e) {
        throw e;
    }
};

export const generateOpenAIWebStory = async (title: string, content: string, apiKey: string, provider: string = 'openai'): Promise<any> => {
    const prompt = `Analise o seguinte post de blog e crie um Web Story (Google Web Stories) com 7 slides. 
        Para cada slide, forneça um título curto (máx 40 caracteres) e um texto curto e impactante (máx 150 caracteres).
        Também sugira um prompt de imagem para cada slide que descreva visualmente o conteúdo.
        
        Título do Post: ${title}
        Conteúdo: ${content.substring(0, 5000)}`;

    try {
        let model = "gpt-4o";
        if (provider === 'deepseek') model = "deepseek-chat";
        if (provider === 'groq') model = "llama3-70b-8192";

        const res = await fetch(getOpenAiUrl(provider), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                model: model,
                messages: [
                    { role: "system", content: "Você é um especialista em Google Web Stories. Responda apenas em JSON válido com a seguinte estrutura: { title: string, excerpt: string, slides: [{ title: string, content: string, imagePrompt: string }] }" },
                    { role: "user", content: prompt }
                ],
                response_format: { type: "json_object" }
            })
        });
        const data = await res.json();
        if (data.error) throw new Error(data.error.message);
        return JSON.parse(data.choices[0]?.message?.content || '{}');
    } catch (e) {
        throw e;
    }
};

export const generateOpenAIRecipe = async (title: string, category: string, apiKey: string, config?: AIContentConfig, siteContext?: string, provider: string = 'openai'): Promise<any> => {
    const system = `${RECIPE_BRAIN}
    
    CONTEXTO DO SITE:
    ${siteContext || 'Não fornecido'}

    INSTRUÇÕES ADICIONAIS (Cérebro do Post):
    - Tom: ${config?.tone || 'Profissional'}
    - Público: ${config?.audience || 'Geral'}
    - Personalização: ${config?.customPrompt || 'Nenhuma'}
    `;
    const user = `Crie uma receita espetacular para: "${title}" na categoria "${category}". Siga rigorosamente todas as instruções do System Prompt para preencher o layout técnico de receitas. IMPORTANTE LIMITAÇÃO DE TAMANHO: Para evitar cortes e JSON quebrado, não gere um conteúdo longo demais (limite máximo razoável de 2000 palavras). Feche o JSON de forma válida.`;
    const res = await callOpenAI(apiKey, system, user, true, provider);
    try {
        const parsed = JSON.parse(res);
        if (parsed.content) {
            parsed.content = cleanAiHtml(parsed.content);
        }
        return parsed;
    } catch (e) {
        console.error(`Erro ao parsear resposta de receita da ${provider}:`, e);
        throw new Error("Formato JSON inválido - Resposta cortada ou mal formatada");
    }
};

export const generateOpenAIBibleStudy = async (title: string, category: string, apiKey: string, config?: AIContentConfig, siteContext?: string, provider: string = 'openai'): Promise<any> => {
    const system = `${BIBLE_STUDY_BRAIN}
    
    CONTEXTO DO SITE:
    ${siteContext || 'Não fornecido'}

    INSTRUÇÕES ADICIONAIS (Cérebro do Post):
    - Tom: ${config?.tone || 'Profissional'}
    - Público: ${config?.audience || 'Geral'}
    - Personalização: ${config?.customPrompt || 'Nenhuma'}
    `;
    const user = `Crie um estudo bíblico ESPETACULAR, PROFUNDO E EXTENSO para: "${title}" na categoria "${category}". Siga rigorosamente todas as instruções do System Prompt para preencher o layout técnico de estudos bíblicos. Por ser um material bíblico, traga um conteúdo VASTO e extremamente rico em detalhes teológicos e exegéticos.`;
    const res = await callOpenAI(apiKey, system, user, true, provider);
    try {
        const parsed = JSON.parse(res);
        if (parsed.content) {
            parsed.content = cleanAiHtml(parsed.content);
        }
        return parsed;
    } catch (e) {
        console.error(`Erro ao parsear resposta de estudo bíblico da ${provider}:`, e);
        throw new Error("Formato JSON inválido - Resposta cortada ou mal formatada");
    }
};
